import { Box, InputAdornment, TextField, Typography } from '@mui/material';
import { DropSlash } from '@phosphor-icons/react';
import React, { useState } from 'react';

interface ColorFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const ColorField: React.FC<ColorFieldProps> = ({
  label,
  value,
  onChange,
  placeholder = '#000000',
}) => {
  const [inputValue, setInputValue] = useState(value);

  const colorPalette = [
    '#F2F4F7',
    '#101828',
    '#475467',
    '#D0D5DD',
    '#66BB6A',
    '#FECA26',
    '#EF5350',
    '#3650DE',
    '#9AAAFF',
    '#9AAAFF',
    '#B589FF',
    'transparent',
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    if (newValue === 'transparent' || /^#[0-9A-Fa-f]{6}$/.test(newValue)) {
      onChange(newValue);
    }
  };

  const handleColorPickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
  };

  const handlePaletteClick = (color: string) => {
    setInputValue(color);
    onChange(color);
  };

  const handleInputBlur = () => {
    if (inputValue !== 'transparent' && !/^#[0-9A-Fa-f]{6}$/.test(inputValue)) {
      setInputValue(value);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <Typography sx={{ fontSize: '12px', color: '#475467' }}>
        {label}
      </Typography>

      <TextField
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        placeholder={placeholder}
        fullWidth
        variant="outlined"
        size="small"
        sx={{
          '& .MuiInputBase-root': {
            height: '32px',
            borderRadius: '4px',
            border: '1px solid #EAECF0',
            backgroundColor: '#F9FAFB',
            color: '#475467',
            margin: '0px',
            fontSize: '14px',
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Box
                sx={{
                  position: 'relative',
                  width: '16px',
                  height: '16px',
                  border: '1px solid #EAECF0',
                  borderRadius: '4px',
                  boxShadow: 'none',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  backgroundColor: value === 'transparent' ? '#f0f0f0' : value,
                  backgroundImage:
                    value === 'transparent'
                      ? 'repeating-conic-gradient(#ccc 0% 25%, transparent 0% 50%) 50% / 16px 16px'
                      : 'none',
                }}
              >
                <input
                  type="color"
                  value={value === 'transparent' ? '#000000' : value}
                  onChange={handleColorPickerChange}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    boxShadow: 'none',
                    width: '16px',
                    height: '16px',
                    opacity: 0,
                    cursor: 'pointer',
                  }}
                />
              </Box>
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Typography sx={{ fontSize: '12px', color: '#475467' }}>
          My Color Palette
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px'}}>
          {colorPalette.map((color, index) => (
            <Box
              key={index}
              onClick={() => handlePaletteClick(color)}
              sx={{
                width: '24px',
                height: '24px',
                borderRadius: '4px',
                cursor: 'pointer',
                border: '1px solid #ccc',
                backgroundColor: color === 'transparent' ? '#f0f0f0' : color,
                backgroundImage:
                  color === 'transparent'
                    ? 'repeating-conic-gradient(#ccc 0% 25%, transparent 0% 50%) 50% / 8px 8px'
                    : 'none',
                '&:hover': {
                  transform: 'scale(1.1)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                },
                transition: 'all 0.2s ease',
              }}
              title={color === 'transparent' ? 'Transparente' : color}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {color === 'transparent' && (
                <DropSlash size={16} color="#98A2B3" />
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ColorField;
